import { ChevronRight } from "lucide-react";
import { useNavigate } from "react-router";
import { StatusDot } from "@/components/StatusDot";
import { EmptyRow, SkeletonRows } from "@/components/TableStates";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { centsEur, statusTone } from "@/lib/documents";
import { date } from "@/lib/format";
import type { DocumentRow } from "@/lib/types";
import { cn } from "@/lib/utils";

const COLS = 7;

function Number({ row }: { row: DocumentRow }) {
  return row.number
    ? <span className="shrink-0 font-medium whitespace-nowrap tabular-nums">{row.number}</span>
    : <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80">Entwurf</span>;
}

export function DocumentTable({ rows, loading, empty }: { rows: DocumentRow[]; loading: boolean; empty: string }) {
  const navigate = useNavigate();
  const open = (id: number) => navigate(`/rechnungen/${id}`);

  return (
    <>
      <div className="hidden md:block">
        <div className="overflow-hidden rounded-lg border bg-card">
          <Table className="table-fixed" containerClassName="overflow-visible">
            <colgroup>
              <col style={{ width: "6.5rem" }} /><col /><col style={{ width: "6.5rem" }} /><col style={{ width: "10.5rem" }} />
              <col style={{ width: "10rem" }} /><col style={{ width: "7.5rem" }} /><col style={{ width: "2.25rem" }} />
            </colgroup>
            <TableHeader>
              <TableRow className="hover:bg-transparent [&>th]:text-muted-foreground">
                <TableHead>Nr.</TableHead><TableHead>Kunde</TableHead><TableHead>Datum</TableHead><TableHead>Leistungszeitraum</TableHead>
                <TableHead>Status</TableHead><TableHead className="text-right">Betrag</TableHead>
                <TableHead><span className="sr-only">Öffnen</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? <SkeletonRows columns={COLS} /> : rows.length === 0 ? <EmptyRow columns={COLS}>{empty}</EmptyRow> : rows.map((r) => (
                <TableRow
                  key={r.id}
                  tabIndex={0}
                  aria-label={`${r.number ?? "Entwurf"} ${r.clientName} öffnen`}
                  onClick={() => open(r.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" && e.target === e.currentTarget) open(r.id); }}
                  className="group/row h-12 cursor-pointer outline-none focus-visible:bg-muted/60"
                >
                  <TableCell><Number row={r} /></TableCell>
                  <TableCell className="overflow-hidden"><span className="block truncate font-medium" title={r.clientName}>{r.clientName}</span></TableCell>
                  <TableCell className="text-muted-foreground tabular-nums">{r.date ? date(r.date) : ""}</TableCell>
                  <TableCell className="overflow-hidden text-muted-foreground tabular-nums"><span className="block truncate" title={r.periodLabel}>{r.periodLabel}</span></TableCell>
                  <TableCell className="overflow-hidden"><StatusDot tone={statusTone(r.status)}>{r.statusLabel}</StatusDot></TableCell>
                  <TableCell className={cn("text-right tabular-nums", statusTone(r.status) === "done" ? "text-muted-foreground" : "font-medium")}>{centsEur(r.totalCents)}</TableCell>
                  <TableCell className="pr-3 pl-0">
                    <ChevronRight aria-hidden className="size-4 text-muted-foreground opacity-0 transition-opacity duration-100 group-hover/row:opacity-100 group-focus-visible/row:opacity-100 motion-reduce:transition-none" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Phones: one card per document instead of seven squeezed columns. */}
      <ul className="overflow-hidden rounded-lg border bg-card md:hidden">
        {loading && <li className="h-14 animate-pulse bg-muted motion-reduce:animate-none" aria-hidden />}
        {!loading && rows.length === 0 && <li className="p-6 text-center text-sm text-muted-foreground">{empty}</li>}
        {rows.map((r) => (
          <li key={r.id} className="border-t first:border-t-0">
            <button type="button" onClick={() => open(r.id)} className="flex min-h-14 w-full items-center gap-3 px-4 py-2.5 text-left text-sm outline-none focus-visible:bg-muted/60 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:ring-inset">
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex items-center gap-2"><Number row={r} /><span className="truncate font-medium">{r.clientName}</span></span>
                <span className="flex min-w-0 gap-2 text-[0.8125rem] text-muted-foreground">
                  <StatusDot tone={statusTone(r.status)} className="shrink-0">{r.statusLabel}</StatusDot>
                  <span className="truncate tabular-nums">{r.date ? date(r.date) : ""}</span>
                </span>
              </span>
              <span className="shrink-0 font-medium tabular-nums">{centsEur(r.totalCents)}</span>
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}
